import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearExpireTime, showIdleModal } from "../store/auth/authSlice";

const IDLE_TIME = 10 * 60 * 1000;     // 3 min (test)
const WARNING_TIME = 2 * 60 * 1000; // 1 min (test)

/**
 * Cross-tab activity stamp.
 *
 * The idle timer used to be per tab, so a tab left open on a dashboard would
 * pop "are you still there?" while the user was actively working in another
 * one — the session was alive, the tab just could not see it. Every tab writes
 * the same key on interaction, and the modal only fires when the NEWEST stamp
 * across all of them is stale.
 *
 * It is a plain timestamp and nothing else: no token, no identity. The server
 * remains the real authority — this only decides when to ask.
 */
export const LAST_ACTIVITY_KEY = "lastActivityAt";

const readLastActivity = () => {
  try {
    const raw = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(raw) ? raw : 0;
  } catch {
    return 0;
  }
};

const writeLastActivity = (at) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    /* storage disabled — fall back to this tab's own clock */
  }
};

// `enabled` lets the app mount this hook from every PrivateRoute while only the
// outermost one actually runs the timers — nested PrivateRoutes would otherwise
// run a second set of idle/expiry timers against the same Redux flag.
export const useSessionManager = (enabled = true) => {
  const dispatch = useDispatch();
  const isModalOpen = useSelector(
    (s) => s.auth.showIdleModal
  );
  // ✅ Separate timers
  const idleTimer = useRef(null);
  const expiryTimer = useRef(null);

  const expireTime = useSelector(
    (s) => s.auth.expireTime
  );

  const isLogin = useSelector(
    (state) => !!state.auth.token
  ) && enabled;

  // -------- Idle Detection --------
  useEffect(() => {
    if (!isLogin) {
      clearTimeout(idleTimer.current);
      clearTimeout(expiryTimer.current);
      return;
    }

    let lastReset = 0;

    /**
     * Re-arm the idle timer against the NEWEST activity in any tab.
     *
     * When the shared stamp is fresher than this tab's own idea of idleness,
     * the timer is set for the remainder of that other tab's window rather
     * than firing here.
     */
    const arm = () => {
      clearTimeout(idleTimer.current);

      const elapsed = Date.now() - (readLastActivity() || Date.now());
      const remaining = Math.max(IDLE_TIME - elapsed, 0);

      idleTimer.current = setTimeout(() => {
        // Re-check at fire time: another tab may have been busy during the
        // wait, which is exactly the case this whole mechanism exists for.
        if (Date.now() - readLastActivity() < IDLE_TIME) {
          arm();
          return;
        }

        if (!isModalOpen) {
          dispatch(showIdleModal("idle"));
        }
      }, remaining || IDLE_TIME);
    };

    const reset = () => {
      const now = Date.now();

      if (now - lastReset < 1000) return;

      lastReset = now;
      writeLastActivity(now);
      arm();
    };

    // Another tab reported activity: stop counting down against our own idea
    // of when the user was last seen.
    const onStorage = (event) => {
      if (event.key === LAST_ACTIVITY_KEY) arm();
    };

    const events = ["mousemove", "keydown", "scroll", "click"];

    events.forEach((e) =>
      window.addEventListener(e, reset)
    );
    window.addEventListener("storage", onStorage);

    reset();

    return () => {
      events.forEach((e) =>
        window.removeEventListener(e, reset)
      );
      window.removeEventListener("storage", onStorage);

      clearTimeout(idleTimer.current);
      clearTimeout(expiryTimer.current);
    };
  }, [isLogin, dispatch]);

  // -------- Expiry Warning --------
  useEffect(() => {
    if (!isLogin || !expireTime) return;

    // ✅ Clear old expiry timer
    clearTimeout(expiryTimer.current);

    const expiryMs = new Date(expireTime).getTime();
    const now = Date.now();

    const warnAt = expiryMs - WARNING_TIME;
    const timeout = warnAt - now;

    if (timeout <= 0) {
      if (!isModalOpen) {
      dispatch(showIdleModal("expiry"));
      dispatch(clearExpireTime());
      }
      return;
    }

    expiryTimer.current = setTimeout(() => {
      if (!isModalOpen) {
      dispatch(showIdleModal("expiry"));
      dispatch(clearExpireTime());
      }
    }, timeout);

  }, [expireTime, isLogin, dispatch]);
};
